import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Policy } from '../../services/policies.service';
import PolicyDocumentEmbed from './PolicyDocumentEmbed';
import './PoliciesList.css';

type Props = {
  policies: Policy[];
  canEdit: boolean;
  onCreate: () => void;
  onEdit: (policy: Policy) => void;
  onToggle: (policy: Policy) => void;
  onDelete: (id: string) => void;
};

function openPolicyInNewWindow(policy: Policy) {
  if (policy.documentUrl) {
    window.open(policy.documentUrl, '_blank', 'noopener,noreferrer');
  } else {
    const w = window.open('', '_blank', 'noopener');
    if (w) {
      w.document.write(
        `<pre style="white-space:pre-wrap;font-family:inherit">${(policy.content || '').replace(/</g, '&lt;')}</pre>`,
      );
      w.document.title = policy.title || 'Policy';
    }
  }
}

const PoliciesList: React.FC<Props> = ({
  policies,
  canEdit,
  onCreate,
  onEdit,
  onToggle,
  onDelete,
}) => {
  const [viewingPolicy, setViewingPolicy] = useState<Policy | null>(null);

  const openPolicy = useCallback((policy: Policy) => {
    if (policy.isActive) {
      setViewingPolicy(policy);
    } else {
      openPolicyInNewWindow(policy);
    }
  }, []);

  useEffect(() => {
    if (!viewingPolicy) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [viewingPolicy]);

  useEffect(() => {
    if (!viewingPolicy) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setViewingPolicy(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewingPolicy]);

  return (
    <div className="sidebar-card policies-section">
      <div className="section-header">
        <h4>Policies & Regulations</h4>
        {canEdit && (
          <button className="add-info-btn" onClick={onCreate} aria-label="Add policy" />
        )}
      </div>
      <div className="policies-list">
        {policies.length === 0 ? (
          <p className="no-items">No policies available</p>
        ) : (
          policies.map((policy) => (
            <div key={policy.id} className={`policy-item${policy.isActive ? '' : ' policy-item--inactive'}`}>
              <div className="policy-item-content">
                <div className="policy-title">
                  <button
                    type="button"
                    className="policy-title-btn"
                    onClick={() => openPolicy(policy)}
                    title={policy.isActive ? 'View policy (shown at login until accepted)' : 'View policy'}
                  >
                    {policy.title}
                  </button>
                </div>
                <div className="policy-type">{policy.type}</div>
                {!policy.isActive && <span className="policy-badge policy-badge--inactive">Inactive</span>}
                {policy.isActive && (
                  <span className="policy-badge">Login acceptance required</span>
                )}
              </div>
              <div className="policy-actions">
                {canEdit
                  ? (
                    <>
                      <button
                        type="button"
                        className="policy-action-btn edit"
                        onClick={() => onEdit(policy)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="policy-action-btn view"
                        onClick={() => openPolicy(policy)}
                        title="View"
                      >
                        👁️
                      </button>
                      <button
                        type="button"
                        className="policy-action-btn toggle"
                        onClick={() => onToggle(policy)}
                        title={policy.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {policy.isActive ? '✅' : '⬜'}
                      </button>
                      <button
                        type="button"
                        className="policy-action-btn delete"
                        onClick={() => onDelete(policy.id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </>
                  )
                  : (
                    <button
                      type="button"
                      className="policy-action-btn view"
                      onClick={() => openPolicy(policy)}
                      title="View"
                    >
                      👁️
                    </button>
                  )}
              </div>
            </div>
          ))
        )}
      </div>

      {viewingPolicy &&
        createPortal(
          <div
            className="policy-view-overlay"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setViewingPolicy(null);
            }}
          >
            <div
              className="policy-view-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="policy-view-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="policy-view-header">
                <div>
                  <h3 id="policy-view-title">{viewingPolicy.title}</h3>
                  <div className="policy-view-meta">
                    <span>{viewingPolicy.type}</span>
                    <span>·</span>
                    <span>
                      {viewingPolicy.isActive ? 'Shown at login until accepted' : 'Reference only'}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="policy-view-close"
                  onClick={() => setViewingPolicy(null)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="policy-view-body">
                {viewingPolicy.documentUrl ? (
                  <div className="policy-view-doc-wrap">
                    <PolicyDocumentEmbed url={viewingPolicy.documentUrl} variant="viewer" />
                  </div>
                ) : null}
                {viewingPolicy.content?.trim() ? (
                  <div
                    className="policy-view-text"
                    dangerouslySetInnerHTML={{
                      __html: viewingPolicy.content.replace(/\n/g, '<br />'),
                    }}
                  />
                ) : null}
                {!viewingPolicy.documentUrl && !viewingPolicy.content?.trim() ? (
                  <div className="policy-view-text">
                    <p>No content attached.</p>
                  </div>
                ) : null}
              </div>
              <div className="policy-view-footer">
                {viewingPolicy.documentUrl && (
                  <a
                    href={viewingPolicy.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in new tab
                  </a>
                )}
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setViewingPolicy(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default PoliciesList;
